import React, { useEffect, useMemo, useCallback } from 'react';
import { Nullable } from 'src/types/nullable.type';
import { StepEditorPreviewPanel } from './preview/panel';
import { StepEditorContentPanel, StepEditorContentPanelProps } from './content/panel';
import { findNodeInStep, findStepInJourney } from 'src/utils/journey';
import { useEditorContext } from 'src/store/editor';
import { useStepEditorContext } from 'src/store/step-editor';
import { apiGetNode } from 'src/utils/journeyApi';
import { StepEditorContentConfigureStageTypeEnum } from './content/configure/stage-type.enum';
import { Node } from 'src/common/interfaces/node.interface';
import { EditorJourneyEdit } from '../journey-edit';
import { StepEditorContentDocumentEditor } from './content/document/editor';
import { useEditorNavigator } from 'src/editor/use-editor-navigator.hook';
import { useJsPdfConverter } from 'src/common/hooks/use-js-pdf-converter.hook';
import { IconButton } from 'src/common/icon-button';
import { DownloadStrongIcon, EditStrongIcon } from 'src/monet/icons';
import { Document } from 'src/common/interfaces/document.interface';
import { TooltipComponent } from 'src/common/tooltip/component';

export interface StepEditorProps extends Omit<StepEditorContentPanelProps, 'node' | 'editMode'> {
  nodeId?: string;
  stepId?: Nullable<string>;
  documentId?: Nullable<string>;
}

export const StepEditor = ({ nodeId, stepId, documentId, activeStage }: StepEditorProps) => {
  const { journey, currentStep, updateNodeInJourney, setCurrentActiveNodeId, setCurrentStep } = useEditorContext();
  const { name, node_id, is_synced, setStateFromNode, currentConfigurationStage, document } = useStepEditorContext();
  const { navigateToContentEdit, navigateToSyncedStepEdit, navigateToDocumentEdit } = useEditorNavigator();

  const { convertHTMLToPdf } = useJsPdfConverter();

  const isNewStepActive = useMemo(
    () => window.location.pathname.endsWith(StepEditorContentConfigureStageTypeEnum.NEW),
    []
  );

  const isEditingJourney = useMemo(() => activeStage === StepEditorContentConfigureStageTypeEnum.BOARD, [activeStage]);

  const activeStageByPathName = useMemo(
    () => (isNewStepActive ? currentConfigurationStage : activeStage),
    [activeStage, isNewStepActive, currentConfigurationStage]
  );

  const onNodeUpdated = useCallback(
    (node: Node) => {
      setCurrentActiveNodeId(node.id);
      setStateFromNode(node);
    },
    [setStateFromNode, setCurrentActiveNodeId]
  );

  useEffect(() => {
    if (journey) {
      const step = findStepInJourney(journey, stepId);
      setCurrentStep(step);

      if (nodeId) {
        const node = findNodeInStep(step, nodeId);
        if (node) {
          onNodeUpdated(node);
        } else {
          // node has been removed or not valid
          navigateToContentEdit(step.id, step.nodes[0].id, { replaceHistory: true });
          return;
        }
      }
    } else {
      if (nodeId) {
        apiGetNode(nodeId).then(onNodeUpdated);
      }
    }
  }, [nodeId, stepId, setCurrentStep, setStateFromNode, journey, onNodeUpdated]);

  const onDocumentUpdate = useCallback(
    (document: Document) => {
      if (document && currentStep && nodeId) {
        const node = findNodeInStep(currentStep, nodeId);
        const updatedNode = {
          ...node,
          document,
        };
        updateNodeInJourney(updatedNode, currentStep);
        onNodeUpdated(updatedNode);
      }
    },
    [nodeId, currentStep, updateNodeInJourney, onNodeUpdated]
  );

  const onDocumentEditorBack = () => {
    if (!node_id) return;

    // if we are in journey context, then navigate back to step edit
    if (journey && currentStep) {
      navigateToContentEdit(currentStep.id, node_id, { replaceHistory: true });
    } else if (is_synced) {
      navigateToSyncedStepEdit(node_id, { replaceHistory: true });
    }
  };

  const downloadHtmlAsPdf = () => {
    const html = window.document.getElementById("journey-document-viewer");
    if (!html) return;


    convertHTMLToPdf(html, `${name}.pdf`);
  }

  const documentEditButton = useMemo(() => {
    if (node_id && document && currentStep) {
      const onClick = () => navigateToDocumentEdit(node_id, document.id, is_synced, currentStep.id);

      return (
        <div className="flex items-center space-x-2">
          <TooltipComponent
            tooltipContent="Download as PDF"
            placement="bottom"
            styling="tooltip"
            theme='journey'>
            <IconButton onClick={downloadHtmlAsPdf}>
              <DownloadStrongIcon />
            </IconButton>
          </TooltipComponent>
          <IconButton onClick={onClick}>
            <EditStrongIcon />
          </IconButton>
        </div>
      );
    }

    return null;
  }, [node_id, document, currentStep, navigateToDocumentEdit, is_synced]);

  if (documentId && document) {
    return (
      <StepEditorContentDocumentEditor
        existingDocument={document}
        onDocumentUpdate={onDocumentUpdate}
        onBack={onDocumentEditorBack}
      />
    );
  }

  return (
    <div className='player-bedrock flex flex-nowrap h-full w-full absolute bg-transparent overflow-hidden'>
      <div className='flex-1 relative min-w-0 min-h-0'>
        <div className='absolute w-full h-full overflow-hidden items-stretch min-h-0'>
          <div className='relative flex flex-1 flex-col h-full min-w-0 md:flex-row bg-bedrock-light-gray '>
            {!isEditingJourney ? (
              <StepEditorContentPanel editMode={!isNewStepActive} activeStage={activeStageByPathName} />
            ) : (
              <EditorJourneyEdit />
            )}
            <StepEditorPreviewPanel isEditingJourney={isEditingJourney} actionButton={documentEditButton} />
          </div>
        </div>
      </div>
    </div>
  );
};
